// pages/PaymentList.js - Updated with separate rows for both party and bank in paid payments

import { useEffect, useState } from "react";
import { database } from "../firebase/firebase";
import { ref, onValue, remove, update, get } from "firebase/database";
import { useNavigate } from "react-router-dom";
import "../styles/main.css";

export default function PaymentList() {
  const navigate = useNavigate();
  
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYear, setFinancialYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [settings, setSettings] = useState({ financialYear: "2025-26" });
  
  // Filters
  const [filterType, setFilterType] = useState("all");
  const [filterParty, setFilterParty] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Statistics
  const [totalReceived, setTotalReceived] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalPayments, setTotalPayments] = useState(0);
  
  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // View mode
  const [viewMode, setViewMode] = useState("table");
  
  // Load settings
  useEffect(() => {
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSettings(prev => ({
          ...prev,
          financialYear: data.financialYear || "2025-26"
        }));
        setFinancialYear(data.financialYear || "2025-26");
      }
    });
  }, []);
  
  // Get available financial years
  useEffect(() => {
    const fetchFinancialYears = async () => {
      const financialYearsRef = ref(database, "financialYears");
      const snapshot = await get(financialYearsRef);
      
      if (snapshot.exists()) {
        const years = Object.keys(snapshot.val());
        setAvailableYears(years.sort().reverse());
      } else {
        setAvailableYears([settings.financialYear]);
      }
    };
    
    fetchFinancialYears();
  }, [settings.financialYear]);
  
  // Load payments
  useEffect(() => {
    if (!financialYear) return;
    
    setLoading(true);
    const paymentsRef = ref(database, `financialYears/${financialYear}/payments`);
    
    const unsubscribe = onValue(paymentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const paymentList = Object.entries(data).map(([id, payment]) => ({
          id,
          ...payment
        }));
        
        paymentList.sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
        
        setPayments(paymentList);
        
        let received = 0;
        let paid = 0;
        paymentList.forEach(p => {
          if (p.paymentType === "received") {
            received += p.amount || 0;
          } else {
            paid += p.amount || 0;
          }
        });
        
        setTotalReceived(received);
        setTotalPaid(paid);
        setTotalPayments(paymentList.length);
      } else {
        setPayments([]);
        setTotalReceived(0);
        setTotalPaid(0);
        setTotalPayments(0);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [financialYear]);
  
  // Apply filters
  useEffect(() => {
    let filtered = [...payments];
    
    if (filterType !== "all") {
      filtered = filtered.filter(p => p.paymentType === filterType);
    }
    
    if (filterParty) {
      filtered = filtered.filter(p => 
        p.partyName?.toLowerCase().includes(filterParty.toLowerCase())
      );
    }
    
    if (startDate) {
      filtered = filtered.filter(p => p.paymentDate >= startDate);
    }
    
    if (endDate) {
      filtered = filtered.filter(p => p.paymentDate <= endDate);
    }
    
    setFilteredPayments(filtered);
  }, [payments, filterType, filterParty, startDate, endDate]);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };
  
  const getPaymentModeIcon = (mode) => {
    const icons = {
      cash: "💵",
      bank: "🏦",
      cheque: "📝",
      upi: "📱",
      card: "💳"
    };
    return icons[mode] || "💰";
  };
  
  // Handle edit payment
  const handleEdit = (payment) => {
    navigate(`/payment/edit/${payment.id}`);
  };
  
  // Handle delete payment
  const handleDelete = async () => {
    if (!paymentToDelete) return;
    
    setDeleteLoading(true);
    try {
      const paymentRef = ref(database, `financialYears/${financialYear}/payments/${paymentToDelete.id}`);
      await remove(paymentRef);
      
      // Update party balance
      if (paymentToDelete.partyId) {
        const partyRef = ref(database, `customers/${paymentToDelete.partyId}`);
        const partySnapshot = await get(partyRef);
        
        if (partySnapshot.exists()) {
          const party = partySnapshot.val();
          let currentBalance = party.openingBalance || party.balance || 0;
          let newBalance;
          
          if (paymentToDelete.paymentType === "received") {
            newBalance = currentBalance + paymentToDelete.amount;
          } else {
            newBalance = currentBalance - paymentToDelete.amount;
          }
          
          await update(partyRef, {
            openingBalance: newBalance,
            balance: newBalance,
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      // Update bank account balance
      if (paymentToDelete.bankAccountId) {
        const bankRef = ref(database, `customers/${paymentToDelete.bankAccountId}`);
        const bankSnapshot = await get(bankRef);
        
        if (bankSnapshot.exists()) {
          const bankAccount = bankSnapshot.val();
          let newBankBalance;
          
          if (paymentToDelete.paymentType === "received") {
            newBankBalance = (bankAccount.balance || 0) - paymentToDelete.amount;
          } else {
            newBankBalance = (bankAccount.balance || 0) + paymentToDelete.amount;
          }
          
          await update(bankRef, {
            balance: newBankBalance,
            openingBalance: newBankBalance,
            updatedAt: new Date().toISOString()
          });
        }
      }
      
      alert("Payment deleted successfully!");
      setShowDeleteModal(false);
      setPaymentToDelete(null);
    } catch (error) {
      console.error("Error deleting payment:", error);
      alert("Failed to delete payment. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Calculate transaction totals
  const transactionTotals = {
    totalReceivedAmount: filteredPayments.filter(p => p.paymentType === "received").reduce((sum, p) => sum + (p.amount || 0), 0),
    totalPaidAmount: filteredPayments.filter(p => p.paymentType === "paid").reduce((sum, p) => sum + (p.amount || 0), 0),
    netAmount: filteredPayments.filter(p => p.paymentType === "received").reduce((sum, p) => sum + (p.amount || 0), 0) - 
               filteredPayments.filter(p => p.paymentType === "paid").reduce((sum, p) => sum + (p.amount || 0), 0)
  };
  
  // Function to render transaction rows with proper alignment
  const renderTransactionRows = (payment) => {
    const rows = [];
    const date = formatDate(payment.paymentDate);
    const voucherNo = payment.paymentNo;
    const mode = `${getPaymentModeIcon(payment.paymentMode)} ${payment.paymentMode.toUpperCase()}`;
    
    if (payment.paymentType === "received") {
      // Row 1: Customer - Credited (Cr)
      rows.push(
        <tr key={`${payment.id}-customer`} className="transaction-row">
          <td className="date-cell">{date}</td>
          <td className="voucher-no">{voucherNo}</td>
          <td className="description-cell">
            <span className="party-name">{payment.partyName}</span>
          </td>
          <td className="amount credit-amount">{formatCurrency(payment.amount)}</td>
          <td className="amount">-</td>
          <td className="mode-cell">{mode}</td>
          <td className="actions">
            <button className="action-btn edit" onClick={() => handleEdit(payment)}>✏️</button>
            <button className="action-btn delete" onClick={() => {
              setPaymentToDelete(payment);
              setShowDeleteModal(true);
            }}>🗑️</button>
          </td>
        </tr>
      );
      
      // Row 2: Bank Account - Debited (Dr)
      if (payment.bankName) {
        rows.push(
          <tr key={`${payment.id}-bank`} className="transaction-row bank-row">
            <td className="date-cell"></td>
            <td className="voucher-no"></td>
            <td className="description-cell bank-line">
              <span className="bank-name">{payment.bankName} account</span>
            </td>
            <td className="amount">-</td>
            <td className="amount debit-amount">{formatCurrency(payment.amount)}</td>
            <td className="mode-cell"></td>
            <td className="actions"></td>
          </tr>
        );
      }
      
    } else {
      // Paid Payment - Two rows (Party Debited, Bank Credited)
      // Row 1: Party/Supplier - Debited (Dr)
      rows.push(
        <tr key={`${payment.id}-supplier`} className="transaction-row">
          <td className="date-cell">{date}</td>
          <td className="voucher-no">{voucherNo}</td>
          <td className="description-cell">
            <span className="party-name">{payment.partyName} account</span>
          </td>
          <td className="amount">-</td>
          <td className="amount debit-amount">{formatCurrency(payment.amount)}</td>
          <td className="mode-cell">{mode}</td>
          <td className="actions">
            <button className="action-btn edit" onClick={() => handleEdit(payment)}>✏️</button>
            <button className="action-btn delete" onClick={() => {
              setPaymentToDelete(payment);
              setShowDeleteModal(true);
            }}>🗑️</button>
          </td>
        </tr>
      );
      
      // Row 2: Bank Account - Credited (Cr) - Money comes from bank
      if (payment.bankName) {
        rows.push(
          <tr key={`${payment.id}-bank`} className="transaction-row bank-row">
            <td className="date-cell"></td>
            <td className="voucher-no"></td>
            <td className="description-cell bank-line">
              <span className="bank-name">{payment.bankName} account</span>
            </td>
            <td className="amount credit-amount">{formatCurrency(payment.amount)}</td>
            <td className="amount">-</td>
            <td className="mode-cell"></td>
            <td className="actions"></td>
          </tr>
        );
      }
    }
    
    // Reference row if exists (for both types)
    if (payment.referenceNo) {
      rows.push(
        <tr key={`${payment.id}-ref`} className="transaction-row reference-row">
          <td className="date-cell"></td>
          <td className="voucher-no"></td>
          <td className="description-cell reference-line">
            <span className="reference-text">Ref: {payment.referenceNo}</span>
          </td>
          <td className="amount"></td>
          <td className="amount"></td>
          <td className="mode-cell"></td>
          <td className="actions"></td>
        </tr>
      );
    }
    
    return rows;
  };
  
  return (
    <div className="payment-list-page">
      {/* Header */}
      <div className="page-header">
        <h2>📋 Payment List</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/")} className="back-btn">
            🏠 Home
          </button>
          <button onClick={() => navigate("/payment/new")} className="add-btn">
            + New Payment
          </button>
        </div>
      </div>
      
      {/* Financial Year Selector */}
      <div className="financial-year-selector">
        <label>Financial Year:</label>
        <select 
          value={financialYear} 
          onChange={(e) => setFinancialYear(e.target.value)}
          className="year-select"
        >
          {availableYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card received">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <div className="stat-label">Total Credited (Received)</div>
            <div className="stat-value">{formatCurrency(totalReceived)}</div>
          </div>
        </div>
        <div className="stat-card paid">
          <div className="stat-icon">💸</div>
          <div className="stat-info">
            <div className="stat-label">Total Debited (Paid)</div>
            <div className="stat-value">{formatCurrency(totalPaid)}</div>
          </div>
        </div>
        <div className="stat-card net">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <div className="stat-label">Net Balance</div>
            <div className="stat-value" style={{ color: totalReceived - totalPaid >= 0 ? "#28a745" : "#dc3545" }}>
              {formatCurrency(totalReceived - totalPaid)}
            </div>
          </div>
        </div>
        <div className="stat-card total">
          <div className="stat-icon">📋</div>
          <div className="stat-info">
            <div className="stat-label">Total Transactions</div>
            <div className="stat-value">{totalPayments}</div>
          </div>
        </div>
      </div>
      
      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={`mode-btn ${viewMode === "table" ? "active" : ""}`}
          onClick={() => setViewMode("table")}
        >
          📋 Table View
        </button>
        <button 
          className={`mode-btn ${viewMode === "card" ? "active" : ""}`}
          onClick={() => setViewMode("card")}
        >
          🃏 Card View
        </button>
      </div>
      
      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Transaction Type:</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Transactions</option>
            <option value="received">Credited (Amount Received)</option>
            <option value="paid">Debited (Amount Paid)</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label>Party Name:</label>
          <input
            type="text"
            placeholder="Search by name..."
            value={filterParty}
            onChange={(e) => setFilterParty(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>From Date:</label>
          <input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <label>To Date:</label>
          <input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        
        <button 
          className="clear-filters"
          onClick={() => {
            setFilterType("all");
            setFilterParty("");
            setStartDate("");
            setEndDate("");
          }}
        >
          Clear Filters
        </button>
      </div>
      
      {/* Transaction Table View */}
      {viewMode === "table" && (
        <div className="transaction-table-container">
          <div className="table-header">
            <h3>Transaction Details</h3>
            <span className="record-count">{filteredPayments.length} records</span>
          </div>
          
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <p>No transactions found</p>
              <button onClick={() => navigate("/payment/new")} className="create-btn">
                + Create New Transaction
              </button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="transaction-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Voucher No.</th>
                      <th>Transaction Description</th>
                      <th className="amount">Credited (Cr)</th>
                      <th className="amount">Debited (Dr)</th>
                      <th>Mode</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map(payment => renderTransactionRows(payment))}
                  </tbody>
                  <tfoot>
                    <tr className="totals-row">
                      <td colSpan="3"><strong>Totals</strong></td>
                      <td className="amount"><strong>{formatCurrency(transactionTotals.totalReceivedAmount)}</strong></td>
                      <td className="amount"><strong>{formatCurrency(transactionTotals.totalPaidAmount)}</strong></td>
                      <td colSpan="2"></td>
                    </tr>
                    <tr className="net-row">
                      <td colSpan="3"><strong>Net Balance</strong></td>
                      <td colSpan="3" className="amount">
                        <strong style={{ color: transactionTotals.netAmount >= 0 ? "#28a745" : "#dc3545" }}>
                          {formatCurrency(transactionTotals.netAmount)}
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      )}
      
      {/* Card View */}
      {viewMode === "card" && (
        <div className="payments-list">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <p>No transactions found</p>
              <button onClick={() => navigate("/payment/new")} className="create-btn">
                + Create New Transaction
              </button>
            </div>
          ) : (
            filteredPayments.map(payment => (
              <div 
                key={payment.id} 
                className={`payment-card ${payment.paymentType}`}
              >
                <div className="payment-header">
                  <div className="payment-type-badge">
                    {payment.paymentType === "received" ? "💰 Credited" : "💸 Debited"}
                  </div>
                  <div className="payment-number">{payment.paymentNo}</div>
                </div>
                
                <div className="payment-body">
                  {payment.paymentType === "received" ? (
                    <>
                      <div className="transaction-line">
                        <span className="party-name">{payment.partyName}</span>
                        <span className="transaction-action"> Credited </span>
                        <span className="amount credit">{formatCurrency(payment.amount)}</span>
                      </div>
                      {payment.bankName && (
                        <div className="bank-transaction-line">
                          <span className="bank-name">{payment.bankName}</span>
                          <span className="transaction-action"> account Debited </span>
                          <span className="amount debit">{formatCurrency(payment.amount)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="transaction-line">
                        <span className="party-name">{payment.partyName}</span>
                        <span className="transaction-action"> account Debited </span>
                        <span className="amount debit">{formatCurrency(payment.amount)}</span>
                      </div>
                      {payment.bankName && (
                        <div className="bank-transaction-line">
                          <span className="bank-name">{payment.bankName}</span>
                          <span className="transaction-action"> account Credited </span>
                          <span className="amount credit">{formatCurrency(payment.amount)}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="payment-details">
                    <div className="detail-item">
                      <span>📅 {formatDate(payment.paymentDate)}</span>
                    </div>
                    <div className="detail-item">
                      <span>{getPaymentModeIcon(payment.paymentMode)} {payment.paymentMode.toUpperCase()}</span>
                    </div>
                    {payment.referenceNo && (
                      <div className="detail-item">
                        <span>🔖 Ref: {payment.referenceNo}</span>
                      </div>
                    )}
                  </div>
                  
                  {payment.notes && (
                    <div className="payment-notes-full">📝 {payment.notes}</div>
                  )}
                </div>
                
                <div className="payment-footer">
                  <div className={`payment-amount ${payment.paymentType}`}>
                    {payment.paymentType === "received" ? "+ " : "- "}
                    {formatCurrency(payment.amount)}
                  </div>
                  <div className="payment-actions">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(payment)}
                    >
                      ✏️ Edit
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => {
                        setPaymentToDelete(payment);
                        setShowDeleteModal(true);
                      }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Transaction</h3>
            <p>Are you sure you want to delete this transaction?</p>
            <div className="payment-details-summary">
              <div><strong>Voucher No:</strong> {paymentToDelete?.paymentNo}</div>
              <div><strong>Transaction:</strong> {paymentToDelete?.partyName} {paymentToDelete?.paymentType === "received" ? "Credited" : "Debited"} {formatCurrency(paymentToDelete?.amount)}</div>
              {paymentToDelete?.bankName && (
                <div><strong>Bank:</strong> {paymentToDelete.bankName} {paymentToDelete?.paymentType === "received" ? "Debited" : "Credited"} {formatCurrency(paymentToDelete?.amount)}</div>
              )}
              <div><strong>Date:</strong> {formatDate(paymentToDelete?.paymentDate)}</div>
            </div>
            <p className="warning-text">⚠️ This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="btn-secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setPaymentToDelete(null);
                }}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button 
                className="btn-delete"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete Transaction"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      <style>{`
        .payment-list-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }
        
        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }
        
        .back-btn, .add-btn {
          background: #f0f0f0;
          color: #333;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
        }
        
        .add-btn {
          background: #5a60ea;
          color: white;
          margin-left: 10px;
        }
        
        .financial-year-selector {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px 20px;
          margin: 10px 20px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 15px;
          font-size: 14px;
        }
        
        .year-select {
          padding: 6px 12px;
          border-radius: 6px;
          border: none;
          background: white;
          font-weight: bold;
          cursor: pointer;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          padding: 0 20px;
          margin-bottom: 20px;
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .stat-icon {
          font-size: 32px;
        }
        
        .stat-info {
          flex: 1;
        }
        
        .stat-label {
          font-size: 11px;
          color: #666;
        }
        
        .stat-value {
          font-size: 16px;
          font-weight: bold;
        }
        
        .stat-card.received .stat-value { color: #28a745; }
        .stat-card.paid .stat-value { color: #dc3545; }
        .stat-card.total .stat-value { color: #5a60ea; }
        
        .view-mode-toggle {
          display: flex;
          margin: 0 20px 20px;
          gap: 10px;
          background: white;
          padding: 5px;
          border-radius: 50px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .view-mode-toggle .mode-btn {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 40px;
          font-size: 13px;
          cursor: pointer;
          background: transparent;
          color: #666;
          transition: all 0.3s;
        }
        
        .view-mode-toggle .mode-btn.active {
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
        }
        
        .filters-section {
          background: white;
          margin: 0 20px 20px;
          padding: 15px;
          border-radius: 12px;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          align-items: flex-end;
        }
        
        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 5px;
          flex: 1;
          min-width: 120px;
        }
        
        .filter-group label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }
        
        .filter-group select, .filter-group input {
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
        }
        
        .clear-filters {
          padding: 10px 20px;
          background: #f0f0f0;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        
        /* Transaction Table Styles */
        .transaction-table-container {
          margin: 0 20px 20px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          border-bottom: 1px solid #eee;
        }
        
        .table-header h3 {
          margin: 0;
          font-size: 16px;
        }
        
        .record-count {
          font-size: 12px;
          color: #666;
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        .transaction-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .transaction-table th,
        .transaction-table td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #f0f0f0;
          font-size: 13px;
          vertical-align: top;
        }
        
        .transaction-table th {
          background: #fafafa;
          font-weight: 600;
          color: #333;
        }
        
        .transaction-table .amount {
          text-align: right;
        }
        
        .date-cell {
          white-space: nowrap;
        }
        
        .voucher-no {
          font-family: monospace;
          font-size: 12px;
          color: #666;
          white-space: nowrap;
        }
        
        .description-cell {
          min-width: 200px;
        }
        
        .party-name {
          font-weight: 600;
          color: #333;
        }
        
        .bank-name {
          font-weight: 500;
          color: #5a60ea;
        }
        
        .bank-line {
          padding-left: 20px;
        }
        
        .reference-line {
          margin-top: 4px;
        }
        
        .reference-text {
          font-size: 11px;
          color: #999;
        }
        
        .credit-amount {
          color: #28a745;
          font-weight: 600;
        }
        
        .debit-amount {
          color: #dc3545;
          font-weight: 600;
        }
        
        .mode-cell {
          white-space: nowrap;
        }
        
        .mode-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #f0f0f0;
          border-radius: 12px;
          font-size: 11px;
          white-space: nowrap;
        }
        
        .actions {
          display: flex;
          gap: 8px;
          white-space: nowrap;
        }
        
        .action-btn {
          padding: 5px 10px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .action-btn.edit {
          background: #e3f2fd;
          color: #1976d2;
        }
        
        .action-btn.edit:hover {
          background: #bbdef5;
        }
        
        .action-btn.delete {
          background: #ffebee;
          color: #dc3545;
        }
        
        .action-btn.delete:hover {
          background: #ffcdd2;
        }
        
        .bank-row td, .reference-row td {
          border-top: none;
        }
        
        .bank-row td:first-child,
        .bank-row td:nth-child(2),
        .bank-row td:nth-child(6),
        .bank-row td:nth-child(7),
        .reference-row td:first-child,
        .reference-row td:nth-child(2),
        .reference-row td:nth-child(4),
        .reference-row td:nth-child(5),
        .reference-row td:nth-child(6),
        .reference-row td:nth-child(7) {
          background: transparent;
        }
        
        .totals-row {
          background: #f5f5f5;
          font-weight: bold;
        }
        
        .net-row {
          background: #e8f0fe;
          font-weight: bold;
        }
        
        /* Card View Styles */
        .payments-list {
          margin: 0 20px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        
        .payment-card {
          background: white;
          border-radius: 12px;
          padding: 15px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          border-left: 4px solid #ddd;
        }
        
        .payment-card.received {
          border-left-color: #28a745;
        }
        
        .payment-card.paid {
          border-left-color: #dc3545;
        }
        
        .payment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        
        .payment-type-badge {
          font-size: 12px;
          font-weight: bold;
          padding: 4px 12px;
          border-radius: 20px;
        }
        
        .payment-card.received .payment-type-badge {
          background: #e8f5e9;
          color: #28a745;
        }
        
        .payment-card.paid .payment-type-badge {
          background: #ffebee;
          color: #dc3545;
        }
        
        .payment-number {
          font-size: 12px;
          color: #666;
          font-family: monospace;
        }
        
        .payment-body {
          margin-bottom: 12px;
        }
        
        .transaction-line {
          margin-bottom: 8px;
          font-size: 14px;
        }
        
        .bank-transaction-line {
          margin-bottom: 8px;
          font-size: 13px;
          padding-left: 12px;
          border-left: 2px solid #5a60ea;
        }
        
        .transaction-action {
          color: #666;
        }
        
        .party-name, .bank-name {
          font-weight: bold;
        }
        
        .party-name {
          color: #333;
        }
        
        .bank-name {
          color: #5a60ea;
        }
        
        .amount.credit {
          color: #28a745;
          font-weight: bold;
        }
        
        .amount.debit {
          color: #dc3545;
          font-weight: bold;
        }
        
        .payment-details {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          margin-top: 8px;
        }
        
        .detail-item {
          font-size: 11px;
          color: #666;
        }
        
        .payment-notes-full {
          margin-top: 8px;
          font-size: 11px;
          color: #999;
          padding-top: 8px;
          border-top: 1px solid #f0f0f0;
        }
        
        .payment-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          padding-top: 10px;
          border-top: 1px solid #f0f0f0;
        }
        
        .payment-amount {
          font-size: 18px;
          font-weight: bold;
        }
        
        .payment-amount.received {
          color: #28a745;
        }
        
        .payment-amount.paid {
          color: #dc3545;
        }
        
        .payment-actions {
          display: flex;
          gap: 8px;
        }
        
        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 24px;
          width: 90%;
          max-width: 400px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .modal-content h3 {
          margin: 0 0 12px 0;
          color: #333;
        }
        
        .payment-details-summary {
          background: #f5f5f5;
          padding: 12px;
          border-radius: 8px;
          margin: 15px 0;
        }
        
        .payment-details-summary div {
          margin-bottom: 5px;
          font-size: 13px;
        }
        
        .warning-text {
          color: #ff4444;
          font-size: 12px;
          margin: 10px 0;
        }
        
        .modal-actions {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }
        
        .btn-secondary {
          flex: 1;
          padding: 10px;
          background: #f0f0f0;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        
        .btn-delete {
          flex: 1;
          padding: 10px;
          background: #dc3545;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
        }
        
        .btn-delete:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .loading-state, .empty-state {
          text-align: center;
          padding: 40px 20px;
          background: white;
          border-radius: 12px;
        }
        
        .empty-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .create-btn {
          margin-top: 15px;
          padding: 10px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 20px;
          cursor: pointer;
        }
        
        .spinner {
          display: inline-block;
          width: 30px;
          height: 30px;
          border: 3px solid #f0f0f0;
          border-top-color: #5a60ea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 10px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }
        
        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 20px;
        }
        
        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>
    </div>
  );
}